import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@breeze-ai/ui-library';
import { useSuspenseQuery } from '@tanstack/react-query';

import { usePlatform } from '../../../../context/PlatformContext';
import { PDFStatus } from '../../../../network/apis/policies/types';
import {
  type Asset,
  AssetsList,
} from '../../../components/FileList/AssetsList/AssetsList';
import { PageSection } from '../../../components/PageSection/PageSection';
import { policiesQueries } from '../../network/queries';
import { useUploadPolicyFile } from '../../policyApiHooks';

const MAX_WAIT_TIME = 20000;

export function PollingFileList({ policyId }: { policyId: string }) {
  const [isTimeoutError, setIsTimeoutError] = useState(false);
  const [uploadedAssets, setUploadedAssets] = useState<Asset[]>([]);
  const { platform, isWtw } = usePlatform();
  const { t } = useTranslation();

  const {
    data: policy,
    isFetching,
    refetch,
  } = useSuspenseQuery({
    ...policiesQueries.details({
      policyId,
    }),
    refetchInterval: ({ state }) => {
      setTimeout(() => {
        setIsTimeoutError(true);
      }, MAX_WAIT_TIME);

      const docsInProgress =
        state.data?.proof_of_cover_status !== PDFStatus.InProgress &&
        state.data?.certificate_status !== PDFStatus.InProgress;

      if (isTimeoutError || docsInProgress) {
        return 0;
      }

      return 4000;
    },
  });

  const { data: files, refetch: refetchUploadedFiles } = useSuspenseQuery({
    ...policiesQueries.files({
      policyId: parseInt(policyId),
    }),
  });

  useEffect(() => {
    files?.map((file) => {
      const asset: Asset = {
        id: file.id,
        name: file.file_name!,
        path: file.url,
        type: file.file_type,
        creationDate: file.created_time,
        testId: `policy-file-${file.file_name}`,
        isError: false,
        isHidden: false,
        isLoading: false,
        isUploading: false,
      };
      setUploadedAssets((prev) => {
        const existingAsset = prev.find((a) => a.name === asset.name);
        if (existingAsset) {
          return prev.map((a) =>
            a.name === asset.name ? { ...a, ...asset } : a,
          );
        }
        return [...prev, asset];
      });
    });
  }, [files]);

  const {
    open_cover,
    certificate_url,
    certificate_status,
    draft_certificate_status,
    draft_certificate_url,
    draft_certificate_created_time,
    proof_of_cover_url,
    proof_of_cover_status,
    issue_date,
    proof_of_cover_created_time,
    permissions,
  } = policy ?? {};

  const isProofOfCoverLoading =
    (proof_of_cover_status === PDFStatus.InProgress && !isTimeoutError) ||
    isFetching;
  const isCertificateLoading =
    (certificate_status === PDFStatus.InProgress && !isTimeoutError) ||
    isFetching;
  const isProofOfCoverTimeOutError =
    proof_of_cover_status === PDFStatus.InProgress &&
    isTimeoutError &&
    !isProofOfCoverLoading;
  const isCertificateTimeOutError =
    certificate_status === PDFStatus.InProgress &&
    isTimeoutError &&
    !isCertificateLoading;
  const isDraftCertificateLoading =
    (draft_certificate_status === PDFStatus.InProgress && !isTimeoutError) ||
    isFetching;
  const isDraftCertificateTimeOutError =
    draft_certificate_status === PDFStatus.InProgress &&
    isTimeoutError &&
    !isDraftCertificateLoading;

  const isAuthorizedToViewCertificate = Boolean(permissions?.show_cert_pdf);
  const policyFiles: Asset[] = [
    {
      path: draft_certificate_url,
      name: t('Draft', {
        ns: 'common',
        context: platform,
      }),
      issueDate: draft_certificate_created_time,
      testId: 'draft-certificate',
      isLoading: isDraftCertificateLoading,
      isError: isDraftCertificateTimeOutError,
      isHidden: !draft_certificate_status || !draft_certificate_url,
      onTryAgain: refetch,
      errorMessage: 'Error loading file. Please try again.',
    },
    {
      path: certificate_url,
      name: t('Certificate', {
        ns: 'common',
        context: platform,
      }),
      issueDate: issue_date,
      testId: 'policy-certificate',
      isLoading: isCertificateLoading,
      isError: isCertificateTimeOutError,
      isHidden: !certificate_status || !isAuthorizedToViewCertificate,
      onTryAgain: refetch,
      errorMessage: 'Error loading file. Please try again.',
    },
    {
      path: proof_of_cover_url,
      name: t('ProofOfCover', {
        ns: 'common',
        context: platform,
      }),
      creationDate: proof_of_cover_created_time,
      testId: 'policy-poc',
      isLoading: isProofOfCoverLoading,
      isError: isProofOfCoverTimeOutError,
      isHidden: !proof_of_cover_url,
      onTryAgain: refetch,
      errorMessage: 'Error loading file. Please try again.',
    },
    {
      // This doc is not produced async like other docs
      path: open_cover?.terms_and_conditions_url,
      name: t('PolicyFullTermsAndConditions', {
        ns: 'common',
        context: platform,
      }),
      testId: 'policy-terms-and-conditions',
      isHidden: !open_cover?.terms_and_conditions_url,
      errorMessage: 'Error loading file. Please try again.',
      onTryAgain: refetch,
    },
  ];

  const handleFileUploadError = (error: {
    fileName?: string;
    message: string;
    status: number;
    data: unknown;
  }) => {
    setUploadedAssets((prev) =>
      prev.map((asset) =>
        asset.name === error?.fileName
          ? {
              ...asset,
              isUploading: false,
              isError: true,
              errorMessage: error.message,
              onTryAgain: () => retryFileUpload(asset),
            }
          : asset,
      ),
    );
  };

  const retryFileUpload = (asset: Asset) => {
    setUploadedAssets((prev) =>
      prev.map((a) =>
        a.name === asset.name
          ? {
              ...a,
              isUploading: true,
              isError: false,
            }
          : a,
      ),
    );

    if (!asset.content) {
      return;
    }

    uploadPolicyFilesMutation.mutate({
      policyId: parseInt(policyId),
      bffUploadPolicyFilePayload: {
        content: asset.content,
        data_type: asset.content.type,
        name: asset.content.name,
        size_in_bytes: asset.content.size,
        type: asset.content.name.split('.').pop() ?? '',
      },
    });
  };

  const handleFileUpload = (files: FileList | null) => {
    if (files === null) {
      return;
    }

    const file = files[0];
    const asset: Asset = {
      name: file.name,
      isUploading: true,
      type: file.name.split('.').pop() ?? '',
      content: file,
    };

    setUploadedAssets((prev) => [...prev, asset]);

    uploadPolicyFilesMutation.mutate({
      policyId: parseInt(policyId),
      bffUploadPolicyFilePayload: {
        content: file,
        data_type: file.type,
        name: file.name,
        size_in_bytes: file.size,
        type: file.name.split('.').pop() ?? '',
      },
    });
  };

  const uploadPolicyFilesMutation = useUploadPolicyFile({
    options: {
      onSuccess: () => {
        refetchUploadedFiles();
      },
      onError: handleFileUploadError,
    },
  });

  return proof_of_cover_status || certificate_status ? (
    <PageSection data-testid="documents">
      <AssetsList
        assets={[...policyFiles, ...uploadedAssets]}
        title={<Typography level="h4">Documents</Typography>}
        onFileUpload={isWtw ? handleFileUpload : undefined}
        isUploading={uploadPolicyFilesMutation.isPending}
      />
    </PageSection>
  ) : null;
}
